import React, { ReactNode, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X } from 'react-feather';
import { isTouchScreen } from 'utilities/touchScreen';

type ModalProps = {
  isOpen: boolean;
  className?: string;
  children: ReactNode;
  closeModal?: () => void;
  /**
   * On a phone the panel rises from the bottom edge, full width; from `sm`
   * up it is the usual centred dialog. For the short forms the tree page
   * opens with one thumb (the add-child password and form).
   *
   * On a touch screen a sheet also opens with focus on itself, not on its
   * first field: a focused field brings the keyboard up over the sheet
   * before its prompt has been read (owner, iPhone 14 Pro). The keyboard
   * comes with the first tap on a field.
   */
  sheet?: boolean;
  /**
   * A "Đóng" ✕ in the top right corner that stays put while the content
   * scrolls, for a panel too tall to reach its own Hủy without scrolling
   * (owner, on the add-child form). It comes after the content in the page,
   * so the dialog's first focus still goes to the first field, and the
   * caller leaves room for it beside the title (`pr-9`).
   */
  closeButton?: boolean;
};

/**
 * A dialog is supposed to have an accessible name. Headless UI takes it from a
 * `Dialog.Title` anywhere inside the panel, so every modal re-exports it under
 * a name that does not leak the library: put it on the heading you were going
 * to render anyway, or on the sentence a confirm dialog asks.
 */
export const ModalTitle = Dialog.Title;

export const ModalContainer = ({
  isOpen,
  className,
  children,
  closeModal,
  sheet = false,
  closeButton = false,
}: ModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  // Left unset, the dialog focuses its first focusable element, usually a
  // field. A field's own `autoFocus` cannot overrule that: the portal joins
  // the page only after the panel's children have mounted, so their focus()
  // lands nowhere.
  const initialFocus = sheet && isTouchScreen() ? contentRef : undefined;

  const handleKeyDown = (event: any) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (closeModal) {
        closeModal();
      }
      return;
    }
  };

  const handleClose = (event?: any) => {
    if (closeModal) {
      closeModal();
    }
    return;
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100]"
          initialFocus={initialFocus}
          onClose={() => {
            handleClose();
          }}
          onClick={(e: any) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-[#000] bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className={`flex min-h-full justify-center text-center ${sheet ? 'items-end p-0 sm:items-center sm:p-4' : 'items-center p-4'}`}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom={sheet ? 'opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95' : 'opacity-0 scale-95'}
                enterTo={sheet ? 'opacity-100 translate-y-0 sm:scale-100' : 'opacity-100 scale-100'}
                leave="ease-in duration-200"
                leaveFrom={sheet ? 'opacity-100 translate-y-0 sm:scale-100' : 'opacity-100 scale-100'}
                leaveTo={sheet ? 'opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95' : 'opacity-0 scale-95'}
              >
                {/* `overflow-hidden` is what rounds the corners off whatever the
                    modal puts inside itself, but on its own it also means a panel
                    that is taller than the screen simply loses its bottom edge --
                    and several callers pass a fixed `h-[...]`, so a long sentence
                    was being cut off rather than making room for itself. The panel
                    is a column that stops at the height of the viewport and the
                    content scrolls inside it. */}
                <Dialog.Panel
                  className={` relative flex transform flex-col overflow-hidden bg-white text-left align-middle shadow-xl transition-all
                  ${sheet ? 'w-full max-h-[calc(100vh-3rem)] rounded-t-2xl sm:max-h-[calc(100vh-2rem)] sm:rounded-2xl' : 'max-h-[calc(100vh-2rem)] rounded-2xl'} ${className}`}
                >
                  <div ref={contentRef} tabIndex={sheet ? -1 : undefined} className="w-full h-full flex-1 overflow-y-auto focus:outline-none">{children}</div>
                  {/* Outside the scrolling box, so it does not scroll away; the
                      panel's own background behind it keeps it legible over
                      whatever scrolls underneath. */}
                  {closeButton && closeModal && (
                    <button
                      type="button"
                      aria-label="Đóng"
                      onClick={closeModal}
                      className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-[inherit] text-brand
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    >
                      <X size={24} aria-hidden="true" />
                    </button>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
